import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Table,
  Td,
  Th,
  useToast,
} from "@/core/ui";
import { ApiError } from "@/core/api/client";
import type { Role } from "@/core/auth/types";
import { useAdminUsers, useCreateLocalUser, useUpdateUserRoles, type LocalUserInput } from "./api";

const ROLES: Role[] = ["player", "author", "reviewer", "tenant_admin"];

export function UsersAdmin() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const users = useAdminUsers(q);
  const update = useUpdateUserRoles();
  return (
    <div className="bt-admin__stack">
      <Card>
        <CardTitle>{t("admin.users.title")}</CardTitle>
        <Input
          placeholder={t("admin.users.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="bt-admin__user-search"
          aria-label={t("admin.users.search")}
        />
        <Table>
          <thead>
            <tr>
              <Th>{t("admin.users.email")}</Th>
              <Th>{t("admin.users.roles")}</Th>
            </tr>
          </thead>
          <tbody>
            {(users.data?.results ?? []).map((u) => (
              <tr key={u.id}>
                <Td>
                  {u.email}{" "}
                  {u.has_password_login ? (
                    <Badge tone="info">{t("admin.users.local_account")}</Badge>
                  ) : null}
                </Td>
                <Td>
                  <div className="bt-admin__role-list">
                    {ROLES.map((role) => (
                      <label key={role} className="bt-admin__role-toggle">
                        <input
                          type="checkbox"
                          checked={u.roles.includes(role)}
                          onChange={(e) => {
                            const roles = e.target.checked
                              ? [...u.roles, role]
                              : u.roles.filter((r) => r !== role);
                            update.mutate({ id: u.id, roles });
                          }}
                        />
                        {t(`roles.${role}`)}
                      </label>
                    ))}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <AddLocalUser />
    </div>
  );
}

const EMPTY: LocalUserInput = { email: "", display_name: "", password: "", roles: ["player"] };

/** Flattens the `errors` map of a validation_error payload into displayable lines. */
function validationMessages(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== "validation_error") return [];
  const errors = error.payload["errors"];
  if (!errors || typeof errors !== "object") return [];
  return Object.values(errors as Record<string, unknown>).flatMap((value) =>
    Array.isArray(value) ? value.map(String) : [String(value)],
  );
}

function AddLocalUser() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const create = useCreateLocalUser();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState<string[]>([]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors([]);
    create.mutate(
      { ...form, email: form.email.trim() },
      {
        onSuccess: (user) => {
          setForm(EMPTY);
          toast(t("admin.users.created", { email: user.email }), "success");
        },
        onError: (error) => {
          const messages = validationMessages(error);
          if (messages.length) setErrors(messages);
          else toast(t("app.error_generic"), "danger");
        },
      },
    );
  };

  return (
    <Card>
      <CardTitle>{t("admin.users.add_local_title")}</CardTitle>
      <Alert tone="info">{t("admin.users.add_local_hint")}</Alert>
      <form onSubmit={submit}>
        <Field label={t("admin.users.email")} htmlFor="local-user-email">
          <Input
            id="local-user-email"
            type="email"
            required
            autoComplete="off"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label={t("admin.users.display_name")} htmlFor="local-user-name">
          <Input
            id="local-user-name"
            autoComplete="off"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </Field>
        <Field
          label={t("admin.users.password")}
          htmlFor="local-user-password"
          hint={t("admin.users.password_hint")}
        >
          <Input
            id="local-user-password"
            type="password"
            required
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <fieldset className="bt-admin__role-fieldset">
          <legend className="bt-label">{t("admin.users.roles")}</legend>
          <div className="bt-admin__role-list">
            {ROLES.map((role) => (
              <label key={role} className="bt-admin__role-toggle">
                <input
                  type="checkbox"
                  checked={form.roles.includes(role)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      roles: e.target.checked
                        ? [...form.roles, role]
                        : form.roles.filter((r) => r !== role),
                    })
                  }
                />
                {t(`roles.${role}`)}
              </label>
            ))}
          </div>
          <p className="bt-field__hint">{t("admin.users.roles_hint")}</p>
        </fieldset>
        {errors.length ? (
          <Alert tone="danger">
            <ul className="bt-admin__error-list">
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <Button
          type="submit"
          className="bt-admin__submit"
          loading={create.isPending}
          disabled={form.roles.length === 0}
        >
          {t("admin.users.create")}
        </Button>
      </form>
    </Card>
  );
}
